import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsSixVertical } from '@phosphor-icons/react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  COLUMN_LABEL_KEY,
  PINNED_COLUMNS,
  type ColumnPref,
} from '../utils/memberColumns';

// EditColumnsDropdown renders the "Redigera kolumner" panel: pinned
// columns at the top (greyed, non-interactive), draggable columns below
// with visibility checkboxes, and a "Reset columns" link at the bottom.
//
// Drag-drop is @dnd-kit/sortable — keyboard-accessible (focus a row,
// space to lift, arrows to move, space to drop) and pointer-friendly.

export interface EditColumnsDropdownProps {
  prefs: readonly ColumnPref[];
  onChange: (next: ColumnPref[]) => void;
  onReset: () => void;
}

export function EditColumnsDropdown({ prefs, onChange, onReset }: EditColumnsDropdownProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = prefs.findIndex((p) => p.id === active.id);
    const newIndex = prefs.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove([...prefs], oldIndex, newIndex));
  };

  const toggle = (id: string) => {
    onChange(
      prefs.map((p) => (p.id === id ? { ...p, visible: !p.visible } : p)),
    );
  };

  return (
    <div className="dropdown" ref={containerRef}>
      <button
        type="button"
        className="btn btn--ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {t('members.editColumns')}
      </button>

      {open && (
        <div className="dropdown__panel" role="dialog" aria-label={t('members.editColumns')}>
          <p className="dropdown__group-label">{t('members.columns.pinnedLabel')}</p>
          <ul className="column-list">
            {PINNED_COLUMNS.map((id) => (
              <li key={id} className="column-row column-row--pinned" aria-disabled="true">
                <span className="column-row__handle column-row__handle--disabled" aria-hidden="true">
                  <DotsSixVertical size={14} />
                </span>
                <span className="column-row__name">{t(COLUMN_LABEL_KEY[id])}</span>
              </li>
            ))}
          </ul>

          <p className="dropdown__group-label">{t('members.columns.availableLabel')}</p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={prefs.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="column-list">
                {prefs.map((pref) => (
                  <SortableColumnRow
                    key={pref.id}
                    pref={pref}
                    onToggle={() => toggle(pref.id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <button
            type="button"
            className="dropdown__reset"
            onClick={() => {
              onReset();
              setOpen(false);
            }}
          >
            {t('members.columns.reset')}
          </button>
        </div>
      )}
    </div>
  );
}

function SortableColumnRow({
  pref,
  onToggle,
}: {
  pref: ColumnPref;
  onToggle: () => void;
}) {
  const { t } = useTranslation('common');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: pref.id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="column-row"
    >
      <button
        type="button"
        className="column-row__handle"
        aria-label={t('members.columns.dragHandleAria', { name: t(COLUMN_LABEL_KEY[pref.id]) })}
        {...attributes}
        {...listeners}
      >
        <DotsSixVertical size={14} />
      </button>
      <label className="column-row__label">
        <input
          type="checkbox"
          checked={pref.visible}
          onChange={onToggle}
        />
        <span>{t(COLUMN_LABEL_KEY[pref.id])}</span>
      </label>
    </li>
  );
}
